/**
 * Cockpit Setup-Fortschritt + Dienste-Status (TQ #5711, Wizard MVP (c)).
 *
 * Patric-Scope-Entscheid 2026-05-31: "Health-Tile + ehrliche Progress-Bar".
 *
 * KEIN fiktives 6-Phasen-Backend (#5136 existiert nicht). Stattdessen werden
 * beide Widgets aus ECHTEN, heute vorhandenen Signalen abgeleitet:
 *
 *   Setup-Progress  — 4 Schritte aus echten Daten:
 *     1. Dienste gebucht      ← getOrgServices().services.length > 0
 *     2. Team eingeladen      ← getMembers().members.length > 1
 *     3. Aria personalisiert  ← getOrgSoulAddendum().filled
 *     4. Dienste laufen       ← getOrgHealth().counts.healthy > 0
 *
 *   Dienste-Status  — Live-Probe via GET /api/org/:slug/health
 *                     (org-scoped, member-read; GetOrgHealth Backend).
 *
 * Bewusst NICHT die im Task-Titel genannten fixen Phasen Mail/Team/Web/ERP —
 * die wuerden Phasen fuer nie gebuchte Dienste anzeigen (#6264-Halluzinations-
 * Trap). Die hier gezeigten Schritte sind immer wahr fuer den realen Tenant.
 */

import { useEffect, useState } from 'react'
import { api, type OrgHealthResponse, type OrgHealthStatus } from '../services/api'
import './OnboardingHealthPanel.css'

interface Props {
  slug: string
}

interface SetupStep {
  key: string
  label: string
  done: boolean
}

const STATUS_LABEL: Record<OrgHealthStatus, string> = {
  healthy: 'läuft',
  degraded: 'eingeschränkt',
  unhealthy: 'gestört',
  unknown: 'unbekannt',
  stopped: 'gestoppt',
}

export default function OnboardingHealthPanel({ slug }: Props) {
  const [steps, setSteps] = useState<SetupStep[] | null>(null)
  const [health, setHealth] = useState<OrgHealthResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [healthError, setHealthError] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      setHealthError(false)

      // Alle Quellen parallel, jede fail-safe — ein einzelner 4xx/5xx darf
      // das Panel nicht killen (das Cockpit-Dashboard bleibt sonst leer).
      const [servicesR, membersR, soulR, healthR] = await Promise.allSettled([
        api.getOrgServices(slug),
        api.getMembers(slug),
        api.getOrgSoulAddendum(slug),
        api.getOrgHealth(slug),
      ])

      if (cancelled) return

      const services =
        servicesR.status === 'fulfilled' ? (servicesR.value?.services ?? []) : []
      const members =
        membersR.status === 'fulfilled' ? (membersR.value?.members ?? []) : []
      const soulFilled =
        soulR.status === 'fulfilled' ? Boolean(soulR.value?.filled) : false

      let healthData: OrgHealthResponse | null = null
      if (healthR.status === 'fulfilled') {
        healthData = healthR.value
      } else {
        setHealthError(true)
      }

      const healthyCount = healthData?.counts?.healthy ?? 0

      setSteps([
        { key: 'services', label: 'Dienste gebucht', done: services.length > 0 },
        { key: 'team', label: 'Team eingeladen', done: members.length > 1 },
        { key: 'aria', label: 'Aria personalisiert', done: soulFilled },
        { key: 'running', label: 'Dienste laufen', done: healthyCount > 0 },
      ])
      setHealth(healthData)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [slug])

  if (loading) {
    return (
      <section className="ohp-panel">
        <div className="ohp-loading">Status wird geladen …</div>
      </section>
    )
  }

  const doneCount = steps?.filter(s => s.done).length ?? 0
  const totalSteps = steps?.length ?? 0
  const allDone = totalSteps > 0 && doneCount === totalSteps
  // Aktueller Schritt = erster nicht-erledigter (fuer dezente Hervorhebung).
  const currentIdx = steps?.findIndex(s => !s.done) ?? -1

  return (
    <section className="ohp-panel">
      {/* ── Setup-Fortschritt ───────────────────────────────────────── */}
      <div className="ohp-block">
        <div className="ohp-header">
          <h2>Einrichtung</h2>
          <span className="ohp-progress-count">
            {allDone ? 'Eingerichtet ✓' : `${doneCount}/${totalSteps}`}
          </span>
        </div>
        <ol className="ohp-steps">
          {steps?.map((step, i) => {
            const state = step.done ? 'done' : i === currentIdx ? 'current' : 'todo'
            return (
              <li key={step.key} className={`ohp-step ohp-step-${state}`}>
                <span className="ohp-step-dot" aria-hidden="true">
                  {step.done ? '✓' : i + 1}
                </span>
                <span className="ohp-step-label">{step.label}</span>
                {i < (steps?.length ?? 0) - 1 && <span className="ohp-step-line" aria-hidden="true" />}
              </li>
            )
          })}
        </ol>
      </div>

      {/* ── Dienste-Status ──────────────────────────────────────────── */}
      <div className="ohp-block">
        <div className="ohp-header">
          <h2>Dienste-Status</h2>
          {health && health.counts.total > 0 && (
            <span className={`ohp-health-summary ohp-health-${health.overall || 'unknown'}`}>
              {health.counts.healthy}/{health.counts.total} ok
            </span>
          )}
        </div>

        {healthError ? (
          <div className="ohp-empty">Status konnte nicht geladen werden.</div>
        ) : !health || health.counts.total === 0 ? (
          <div className="ohp-empty">Noch keine prüfbaren Dienste vorhanden.</div>
        ) : (
          <div className="ohp-health-grid">
            {health.services.map(svc => (
              <div
                key={svc.slug}
                className={`ohp-health-tile ohp-health-${svc.status}`}
                title={
                  svc.error
                    ? `${svc.name}: ${STATUS_LABEL[svc.status]} (${svc.error})`
                    : `${svc.name}: ${STATUS_LABEL[svc.status]}`
                }
              >
                <span className="ohp-health-dot" aria-hidden="true" />
                <span className="ohp-health-name">{svc.name}</span>
                <span className="ohp-health-state">{STATUS_LABEL[svc.status]}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
